import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Moon, RefreshCw, Smartphone, Monitor, Sun } from "lucide-react";
import { renderPreview } from "@/lib/emailApi";
import { darkVersion, previewDocument } from "@/lib/emailPreview";
import { hasOp, pendingModules } from "@/lib/stubs";
import type { EmailPreview, PreviewRender } from "@/lib/types";
import { cn } from "@/lib/utils";
import { SampleBadge } from "@/components/SampleBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const WIDTHS = [
  { key: "phone", label: "Phone", px: 375, icon: Smartphone },
  { key: "desktop", label: "Desktop", px: 700, icon: Monitor },
] as const;

/**
 * Light and dark side by side, at phone and desktop widths (CONTRACTS §7a). Exact images from the
 * email module's renderer (the same renderer and merge data as the send) when it is available;
 * until then a quick in-browser approximation, labeled as such.
 */
export function EmailPreviewPanel({
  html,
  brandId,
  subject,
  preheader,
  emailId,
  kind,
}: {
  html: string;
  brandId: number;
  subject: string;
  preheader?: string;
  emailId?: number;
  /** For unsaved HTML: broadcast (the renderer adds the marketing footer), automation or transactional (no footer). */
  kind?: "broadcast" | "automation" | "transactional";
}) {
  const server = useQuery({ queryKey: ["has-op", "email_preview_render"], queryFn: () => hasOp("email_preview_render"), staleTime: Infinity });
  const [nonce, setNonce] = useState(0);
  const exact = useQuery({
    queryKey: ["email-preview", emailId, emailId ? "" : html, brandId, subject, preheader, kind, nonce],
    queryFn: () => renderPreview(emailId ? { email_id: emailId } : { html, brand_id: brandId, subject, preheader, kind }),
    enabled: server.data === true && (!!emailId || html.trim().length > 0),
    staleTime: Infinity,
  });
  if (!html.trim() && !emailId) return <p className="text-sm text-[var(--color-muted-foreground)]">Paste the email's HTML to see it here.</p>;
  if (server.data === true) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
          <span>Exact previews from the same renderer and merge data as the send.</span>
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setNonce((n) => n + 1)} disabled={exact.isFetching}>
            <RefreshCw className={cn(exact.isFetching && "animate-spin")} /> Refresh
          </Button>
        </div>
        {exact.isLoading && <p className="text-sm text-[var(--color-muted-foreground)]">Rendering light and dark versions…</p>}
        {exact.error && <p className="text-sm text-[var(--color-destructive)]">{exact.error.message}</p>}
        {exact.data && exact.data.renders.length > 0 && <ExactRenders p={exact.data} />}
        {exact.data && exact.data.renders.length === 0 && (
          <>
            <p className="text-xs text-[var(--color-warning-fg)]" title={exact.data.note}>
              Exact preview images aren't available on this server yet (no preview renderer is set up). Below is a quick preview in this browser; real mail apps,
              dark mode especially, may show it differently.
            </p>
            <QuickPreview html={html} explained />
          </>
        )}
      </div>
    );
  }
  return <QuickPreview html={html} />;
}

function ExactRenders({ p }: { p: EmailPreview }) {
  const byName = new Map(p.renders.map((r) => [r.name, r]));
  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Dark version: {p.dark_rule}
        {p.warnings.length > 0 && <span className="text-[var(--color-warning-fg)]"> {p.warnings.join(" ")}</span>}
      </p>
      {WIDTHS.map((w) => {
        const light = byName.get(`${w.key}_light`) ?? byName.get(w.key);
        const dark = byName.get(`${w.key}_dark`);
        return (
          <div key={w.key}>
            <div className="mb-1 flex items-center gap-1.5 text-xs font-medium">
              <w.icon className="size-3.5" /> {w.label} ({w.px} px)
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {light && <RenderImage r={light} label={dark ? "Light" : "Light and dark"} />}
              {dark && <RenderImage r={dark} label="Dark" />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RenderImage({ r, label }: { r: PreviewRender; label: string }) {
  return (
    <figure className="overflow-hidden rounded-md border">
      <figcaption className="flex items-center gap-1.5 border-b bg-[var(--color-muted)] px-2 py-1 text-xs">
        {label === "Dark" ? <Moon className="size-3" /> : <Sun className="size-3" />} {label}
        {r.truncated && <Badge variant="muted">cut off at the bottom</Badge>}
      </figcaption>
      {r.data ? (
        <img src={`data:${r.mime_type};base64,${r.data}`} alt={r.caption} className="block h-auto w-full" />
      ) : (
        <p className="p-3 text-xs text-[var(--color-muted-foreground)]">No image returned.</p>
      )}
    </figure>
  );
}

function QuickPreview({ html, explained = false }: { html: string; explained?: boolean }) {
  const [debounced, setDebounced] = useState(html);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(html), 400);
    return () => clearTimeout(t);
  }, [html]);
  const light = useMemo(() => previewDocument(debounced), [debounced]);
  const dark = useMemo(() => darkVersion(light), [light]);
  return (
    <div className="space-y-3">
      {!explained && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
          <SampleBadge stream={pendingModules.email} />
          <span>Quick preview in this browser. Exact previews (the same renderer as the send) appear here when the email module is merged.</span>
        </div>
      )}
      <p className="text-xs text-[var(--color-muted-foreground)]">Dark version: {dark.explanation}</p>
      {WIDTHS.map((w) => (
        <div key={w.key}>
          <div className="mb-1 flex items-center gap-1.5 text-xs font-medium">
            <w.icon className="size-3.5" /> {w.label} ({w.px} px)
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Frame doc={light} width={w.px} label="Light" />
            <Frame doc={dark.html} width={w.px} label="Dark" dark />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A sandboxed frame (no scripts, no access to the admin) shown at the email's real width, scaled to fit.
 *  Also used for the product thank-you page preview (features/billing/AfterPurchaseCard). */
export function Frame({
  doc,
  width,
  label,
  dark,
  height = 640,
  scheme,
}: {
  doc: string;
  width: number;
  label: string;
  dark?: boolean;
  height?: number;
  /** Sets the frame's color-scheme, so a page that follows the visitor's light or dark setting
   *  (prefers-color-scheme) shows that version. */
  scheme?: "light" | "dark";
}) {
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [avail, setAvail] = useState(width);
  useEffect(() => {
    if (!box) return;
    const ro = new ResizeObserver(() => setAvail(box.clientWidth));
    ro.observe(box);
    setAvail(box.clientWidth);
    return () => ro.disconnect();
  }, [box]);
  const scale = Math.min(1, avail / width);
  return (
    <figure className="overflow-hidden rounded-md border">
      <figcaption className="flex items-center gap-1.5 border-b bg-[var(--color-muted)] px-2 py-1 text-xs">
        {dark ? <Moon className="size-3" /> : <Sun className="size-3" />} {label}
      </figcaption>
      <div ref={setBox} style={{ height: height * scale }} className={cn("relative overflow-hidden", dark ? "bg-black" : "bg-white")}>
        <iframe
          title={`${label} preview, ${width} px wide`}
          sandbox=""
          srcDoc={doc}
          style={{ width, height, transform: `scale(${scale})`, transformOrigin: "0 0", border: 0, background: dark ? "#000" : "#fff", colorScheme: scheme }}
        />
      </div>
    </figure>
  );
}
